import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Eye, Filter, AArrowDown, AArrowUp } from "lucide-react";
import { convertDateToCT, formatNumberToDollar, countActiveFiltersAndSort } from "../../utils/util";
import { listAllPayouts } from "../../reducers/payouts.slice";
import { AuthContext } from "../../context/authContext";
import { Button } from "../../components/ui/button";
import DataTable from "../../components/shared/DataTable";
import PageHeader from "../../components/shared/PageHeader";
import SearchBar from "../../components/shared/SearchBar";
import RefreshButton from "../../components/shared/RefreshButton";
import StatusBadge from "../../components/shared/StatusBadge";
import useMediaQuery from "../../components/shared/useMediaQuery";
import ViewPayoutPage from "./PayoutView";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
} from "../../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

const PAYOUT_STATUSES = [
  "PAYMENT_PENDING", "CREDIT_PENDING", "EARLY_PAYMENT", "EARLY_CREDIT",
  "PAYMENT_READY", "CREDIT_READY", "PAYMENT_DENIED", "CREDIT_DENIED",
  "PAYMENT_DONE", "CREDIT_DONE", "RECEIVING_DELAY",
];
const PAYOUT_TYPES = ["PayPal", "ACH"];
const CHUNK_SIZE = 50;

const statStyle = (accent) => ({
  background: "linear-gradient(180deg, rgba(255,255,255,0.025), transparent)",
  border: "1px solid rgba(255,255,255,0.06)",
  borderTop: `3px solid ${accent}`,
  borderRadius: 14,
  padding: "16px 18px",
});

const statLabelStyle = {
  display: "block",
  color: "#c3c8cc",
  fontSize: 10.5,
  fontWeight: 800,
  letterSpacing: "0.09em",
};

const statValueStyle = {
  display: "block",
  color: "#fff",
  fontFamily: "ui-monospace, SF Mono, Menlo, monospace",
  fontSize: 24,
  fontWeight: 800,
  marginTop: 8,
};

const ManagementPayoutPage = () => {
  const dispatch = useDispatch();
  const { showToast } = useContext(AuthContext);

  const [showLoader, setShowLoader] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [allLoadedData, setAllLoadedData] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState(() => {
    const s = sessionStorage.getItem("payouts_selectedStatuses"); return s ? JSON.parse(s) : [];
  });
  const [selectedTypes, setSelectedTypes] = useState(() => {
    const s = sessionStorage.getItem("payouts_selectedTypes"); return s ? JSON.parse(s) : [];
  });
  const [sortDirection, setSortDirection] = useState(() => sessionStorage.getItem("payouts_sortDirection") || null);
  const [prevSortDirection, setPrevSortDirection] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [loadingChunk, setLoadingChunk] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const s = sessionStorage.getItem("payouts_rowsPerPage"); return s ? JSON.parse(s) : 5;
  });
  const [totalRows, setTotalRows] = useState(0);
  const [isNavigatingToPage, setIsNavigatingToPage] = useState(false);
  const [pageCache, setPageCache] = useState(new Map());
  const [currentChunk, setCurrentChunk] = useState(0);
  const [viewPayout, setViewPayout] = useState(null);
  const isInitialRender = useRef(true);
  const isMobile = useMediaQuery("(max-width: 640px)");

  const loadDataChunk = async (chunkNumber = 0) => {
    const filterKey = JSON.stringify({
      selectedStatuses: [...selectedStatuses].sort(),
      selectedTypes: [...selectedTypes].sort(),
      search: searchTerm,
      sortDirection,
    });
    const cacheKey = `chunk-${chunkNumber}-${filterKey}`;
    if (pageCache.has(cacheKey)) {
      const cached = pageCache.get(cacheKey);
      if (cached.totalRows !== undefined) setTotalRows(cached.totalRows);
      return cached.data;
    }
    if (loadingChunk) return [];
    setLoadingChunk(true);
    if (chunkNumber === 0 && allLoadedData.length === 0) setShowLoader(true);
    try {
      const payload = {
        skip: chunkNumber * CHUNK_SIZE,
        limit: CHUNK_SIZE,
        payout_status: selectedStatuses.length > 0 ? selectedStatuses : [],
        type: selectedTypes.length > 0 ? selectedTypes : [],
      };
      if (searchTerm?.trim()) payload.search = searchTerm.trim();
      if (sortDirection && sortDirection !== "null") { payload.sort_by = "AMOUNT"; payload.sort_order = sortDirection; }
      const res = await dispatch(listAllPayouts(payload));
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        showToast(res?.error?.message, "error"); setHasMoreData(false); setFilteredData([]); return [];
      }
      if (!res?.payload?.data || !Array.isArray(res.payload.data)) { setHasMoreData(false); setFilteredData([]); return []; }
      if ((chunkNumber === 0 || !totalRows) && res?.payload?.total) setTotalRows(res.payload.total);
      const formatted = res.payload.data.map((item) => ({
        ...item,
        member_full_name: item?.user_details?.first_name + " " + item?.user_details?.last_name,
        shipmentId: item?.shipment_details?.id,
        totalAmount: formatNumberToDollar(item?.amount),
        shipmentStatus: item?.shipment_details?.status,
        shipmentType: item?.shipment_details?.shipment_type,
        shipmentDate: item?.shipment_details?.shipment_date && !isNaN(new Date(item?.shipment_details?.shipment_date).getTime())
          ? convertDateToCT(item?.shipment_details?.shipment_date, "MMMM D, YYYY hh:mm:ss A")
          : "—",
        paymentDate: item?.payment_date && !isNaN(new Date(item?.payment_date).getTime())
          ? convertDateToCT(item?.payment_date, "MMMM D, YYYY hh:mm:ss A")
          : "—",
      }));
      setPageCache((prev) => new Map(prev.set(cacheKey, { data: formatted, timestamp: Date.now(), totalRows: res.payload.total })));
      setHasMoreData(formatted.length === CHUNK_SIZE);
      return formatted;
    } catch (error) {
      showToast("Error fetching data", "error"); setHasMoreData(false); return [];
    } finally {
      setLoadingChunk(false); setShowLoader(false);
    }
  };

  const loadInitialData = async () => {
    const chunk = await loadDataChunk(0);
    setAllLoadedData(chunk); setCurrentChunk(0);
  };

  const checkAndLoadMoreData = async (page, rpp) => {
    const required = (page + 1) * rpp;
    if (allLoadedData.length >= required || allLoadedData.length >= totalRows || !hasMoreData || loadingChunk) return;
    try {
      setIsNavigatingToPage(true);
      const nextChunk = Math.floor(allLoadedData.length / CHUNK_SIZE);
      const newData = await loadDataChunk(nextChunk);
      if (newData?.length > 0) {
        await new Promise((resolve) => {
          setAllLoadedData((prev) => { const updated = [...prev, ...newData]; resolve(updated); return updated; });
        });
        const pageData = [...allLoadedData, ...newData].slice(page * rpp, page * rpp + rpp);
        setCurrentPage(page); setFilteredData(pageData);
      }
    } catch (error) { showToast("Error loading data", "error"); }
    finally { setIsNavigatingToPage(false); }
  };

  const handleChangePage = async (newPage) => {
    if (isNavigatingToPage || loadingChunk) return;
    const required = (newPage + 1) * rowsPerPage;
    try {
      setIsNavigatingToPage(true);
      if (allLoadedData.length < required && hasMoreData) { await checkAndLoadMoreData(newPage, rowsPerPage); return; }
      setCurrentPage(newPage);
      setFilteredData(allLoadedData.slice(newPage * rowsPerPage, newPage * rowsPerPage + rowsPerPage));
    } catch (error) { showToast("Error loading page data", "error"); }
    finally { setIsNavigatingToPage(false); }
  };

  const handleChangeRowsPerPage = async (n) => {
    setRowsPerPage(n); sessionStorage.setItem("payouts_rowsPerPage", JSON.stringify(n)); setCurrentPage(0);
    if (allLoadedData.length < n && hasMoreData && !loadingChunk) checkAndLoadMoreData(0, n);
  };

  const applyFilters = async () => {
    try {
      setLoadingChunk(true); setCurrentPage(0); setAllLoadedData([]);
      sessionStorage.setItem("payouts_selectedStatuses", JSON.stringify(selectedStatuses));
      sessionStorage.setItem("payouts_selectedTypes", JSON.stringify(selectedTypes));
      sessionStorage.setItem("payouts_sortDirection", sortDirection);
      const results = await loadDataChunk(0);
      if (results?.length > 0) { setFilteredData(results.slice(0, rowsPerPage)); setAllLoadedData(results); }
      else { setFilteredData([]); setAllLoadedData([]); }
    } catch (error) { showToast("Error applying filters", "error"); }
    finally { setLoadingChunk(false); }
  };

  const handleRefresh = () => {
    const currentPageBeforeRefresh = currentPage;
    setPageCache(new Map()); setAllLoadedData([]); setHasMoreData(true);
    loadInitialData().then(() => {
      if (currentPageBeforeRefresh > 0) setTimeout(() => setCurrentPage(currentPageBeforeRefresh), 100);
    });
  };

  useEffect(() => { loadInitialData(); }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 500);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    if (isInitialRender.current) { isInitialRender.current = false; return; }
    if (isSearching) return;
    const run = async () => {
      setIsSearching(true); setCurrentPage(0); setAllLoadedData([]);
      const results = await loadDataChunk(0);
      if (results?.length > 0) setAllLoadedData(results); else { setFilteredData([]); setAllLoadedData([]); }
      setIsSearching(false);
    };
    run();
  }, [debouncedSearch]);

  useEffect(() => {
    if (isNavigatingToPage || loadingChunk || !allLoadedData?.length) return;
    const pageData = allLoadedData.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage);
    if (pageData.length > 0) setFilteredData(pageData);
    else if (currentPage > 0) setCurrentPage((p) => p - 1);
  }, [rowsPerPage, allLoadedData]);

  const handleViewQuote = (row) => {
    sessionStorage.removeItem("tablePage");
    setViewPayout(row);
  };

  const activeFilterCount = countActiveFiltersAndSort(sortDirection, selectedStatuses, selectedTypes);

  const payoutStats = useMemo(() => {
    const PENDING_STATUSES = ["PAYMENT_PENDING", "CREDIT_PENDING", "RECEIVING_DELAY"];
    const READY_STATUSES = ["PAYMENT_READY", "CREDIT_READY", "EARLY_PAYMENT", "EARLY_CREDIT"];
    const COMPLETED_STATUSES = ["PAYMENT_DONE", "CREDIT_DONE"];
    const DENIED_STATUSES = ["PAYMENT_DENIED", "CREDIT_DENIED"];
    return {
      pending: allLoadedData.filter((d) => PENDING_STATUSES.includes(d.status)).length,
      ready: allLoadedData.filter((d) => READY_STATUSES.includes(d.status)).length,
      completed: allLoadedData.filter((d) => COMPLETED_STATUSES.includes(d.status)).length,
      denied: allLoadedData.filter((d) => DENIED_STATUSES.includes(d.status)).length,
    };
  }, [allLoadedData]);

  const formatStatus = (status) =>
    status === "CREDIT_DONE"
      ? "Credited"
      : status?.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");

  const columns = [
    { key: "id", label: "ID", width: 80 },
    {
      key: "member_full_name",
      label: "Member Name",
      width: 150,
      render: (row) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ fontSize: "0.875rem" }}>{row?.member_full_name}</span>
          <span style={{ fontSize: "0.75rem", color: "#8b9296" }}>
            {Array.isArray(row?.user_details?.discord_role) && row.user_details.discord_role.length > 0
              ? row.user_details.discord_role[0]
              : "NA"}
          </span>
        </div>
      ),
    },
    {
      key: "shipmentId",
      label: "Shipment ID & Date",
      width: 180,
      render: (row) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ fontSize: "0.875rem" }}>{row?.shipmentId}</span>
          <span style={{ fontSize: "0.875rem", color: "#8b9296" }}>{row?.shipmentDate || "NA"}</span>
        </div>
      ),
    },
    { key: "shipmentStatus", label: "Shipment Status", width: 130 },
    { key: "invoice_number", label: "Invoice Number", width: 130 },
    { key: "totalAmount", label: "Amount", width: 100, align: "right" },
    {
      key: "status",
      label: "Status",
      width: 140,
      align: "center",
      render: (row) => <StatusBadge status={row?.status} label={formatStatus(row?.status)} />,
    },
    {
      key: "action",
      label: "Action",
      width: 70,
      align: "center",
      render: (row) => (
        <Button variant="ghost" size="icon-sm" onClick={() => handleViewQuote(row)} title="View Details">
          <Eye size={15} />
        </Button>
      ),
    },
  ];

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", width: "100%" }}>
      <div style={{ marginBottom: 16 }}>
        <PageHeader
          title="Wallet Credit Payouts"
          subtitle="Keep track of all orders and wallet payout requests at a glance."
        />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? "130px" : "160px"}, 1fr))`,
          gap: isMobile ? 10 : 14,
          marginBottom: 20,
        }}
      >
        <div style={statStyle("#e8b84b")}>
          <span style={statLabelStyle}>PENDING</span>
          <span style={statValueStyle}>{payoutStats.pending}</span>
        </div>
        <div style={statStyle("#00ADB5")}>
          <span style={statLabelStyle}>READY</span>
          <span style={statValueStyle}>{payoutStats.ready}</span>
        </div>
        <div style={statStyle("#4caf50")}>
          <span style={statLabelStyle}>COMPLETED</span>
          <span style={statValueStyle}>{payoutStats.completed}</span>
        </div>
        <div style={statStyle("#f27059")}>
          <span style={statLabelStyle}>DENIED</span>
          <span style={statValueStyle}>{payoutStats.denied}</span>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "flex-end",
          alignItems: isMobile ? "stretch" : "center",
          gap: 10,
          flexWrap: "wrap",
          marginBottom: 16,
        }}
      >
        {!isMobile && <RefreshButton onRefresh={handleRefresh} spinning={showLoader || loadingChunk} />}
        <div style={{ flex: isMobile ? 1 : "none" }}>
          <SearchBar placeholder="Search payouts…" searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
        </div>
        <DropdownMenu onOpenChange={(open) => { if (!open) applyFilters(); }}>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="default" style={{ width: isMobile ? "100%" : "auto" }}>
              <Filter size={14} /> Filter & Sort
              {activeFilterCount > 0 && (
                <span
                  style={{
                    marginLeft: 4,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: 16,
                    height: 16,
                    borderRadius: 8,
                    background: "#00ADB5",
                    color: "#000",
                    fontSize: 10,
                    fontWeight: 800,
                    padding: "0 4px",
                  }}
                >
                  {activeFilterCount}
                </span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" style={{ minWidth: 240 }}>
            <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {PAYOUT_STATUSES.map((s) => (
              <DropdownMenuCheckboxItem
                key={s}
                checked={selectedStatuses.includes(s)}
                onSelect={(e) => e.preventDefault()}
                onCheckedChange={(checked) =>
                  setSelectedStatuses((prev) => (checked ? [...prev, s] : prev.filter((x) => x !== s)))
                }
              >
                {s.replace(/_/g, " ")}
              </DropdownMenuCheckboxItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Filter by Type</DropdownMenuLabel>
            {PAYOUT_TYPES.map((t) => (
              <DropdownMenuCheckboxItem
                key={t}
                checked={selectedTypes.includes(t)}
                onSelect={(e) => e.preventDefault()}
                onCheckedChange={(checked) =>
                  setSelectedTypes((prev) => (checked ? [...prev, t] : prev.filter((x) => x !== t)))
                }
              >
                {t}
              </DropdownMenuCheckboxItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Sort by Amount</DropdownMenuLabel>
            <DropdownMenuItem active={sortDirection === "ASC"} onClick={() => { setPrevSortDirection(sortDirection); setSortDirection((prev) => prev === "ASC" ? null : "ASC"); }}>
              <AArrowUp size={14} style={{ marginRight: 6 }} /> Low to High
            </DropdownMenuItem>
            <DropdownMenuItem active={sortDirection === "DESC"} onClick={() => { setPrevSortDirection(sortDirection); setSortDirection((prev) => prev === "DESC" ? null : "DESC"); }}>
              <AArrowDown size={14} style={{ marginRight: 6 }} /> High to Low
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <DataTable
        columns={columns}
        rows={filteredData}
        totalRows={totalRows}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        loading={loadingChunk || isNavigatingToPage}
        minWidth={1000}
      />
      <Dialog open={Boolean(viewPayout)} onOpenChange={(open) => !open && setViewPayout(null)}>
        <DialogContent style={{ maxWidth: 860, width: isMobile ? "calc(100vw - 16px)" : "92vw", maxHeight: "90vh", overflowY: "auto" }}>
          <DialogHeader>
            <DialogTitle>Payout Summary</DialogTitle>
            <DialogDescription>Review payout details, shipment summary, invoice details, and approval actions.</DialogDescription>
          </DialogHeader>
          {viewPayout && (
            <ViewPayoutPage
              dialogMode
              payoutDetail={viewPayout}
              onUpdated={() => {
                setViewPayout(null);
                handleRefresh();
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManagementPayoutPage;
